import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import type { ReactNode } from 'react';

/**
 * Eén blijvend meldgebied voor de toestand van de module, en de haak waarmee een toestand erin
 * schrijft.
 *
 * De reden dat dit bestaat: `role="status"` en `role="alert"` op een element dat sámen met zijn
 * tekst in de pagina verschijnt, is een levend gebied dat pas ontstaat op het moment dat het iets
 * te melden heeft. Voor `alert` gaat dat in de praktijk goed; voor `status` niet betrouwbaar, en
 * de drie toestanden van deze module vervangen elkáár — er is geen moment waarop het ene gebied
 * blijft staan en zijn inhoud verandert. Wie met een schermlezer werkt, hoort dan dat het scherm
 * verspringt zonder te horen waarom.
 *
 * Dit gebied staat in de schil en gaat nooit weg. De toestanden schrijven er hun zin in; het
 * zichtbare blok blijft daarnaast staan zoals het staat, want de zin is voor wie het beeld niet
 * ziet en niet in plaats van het beeld.
 */

interface Aankondiging {
  zin: string;
  /** Telt elke aankondiging, zodat dezelfde zin twee keer achter elkaar ook twee keer klinkt. */
  sleutel: number;
}

interface Melder {
  zin: string;
  meld: (zin: string | null) => void;
  aankondiging: Aankondiging;
  kondigAan: (zin: string) => void;
}

const Context = createContext<Melder>({ zin: '', meld: () => {}, aankondiging: { zin: '', sleutel: 0 }, kondigAan: () => {} });

export function ToestandsmeldingProvider({ children }: { children: ReactNode }) {
  const [zin, setZin] = useState('');
  const [aankondiging, setAankondiging] = useState<Aankondiging>({ zin: '', sleutel: 0 });
  const meld = useCallback((nieuw: string | null) => setZin(nieuw ?? ''), []);
  const kondigAan = useCallback((nieuw: string) => setAankondiging((vorige) => ({ zin: nieuw, sleutel: vorige.sleutel + 1 })), []);
  // Een aankondiging hoort bij de pagina waarop zij viel: een tabwissel ruimt haar op.
  const { pathname } = useLocation();
  useEffect(() => setAankondiging((vorige) => (vorige.zin === '' ? vorige : { zin: '', sleutel: vorige.sleutel + 1 })), [pathname]);
  const waarde = useMemo(() => ({ zin, meld, aankondiging, kondigAan }), [zin, meld, aankondiging, kondigAan]);

  return <Context.Provider value={waarde}>{children}</Context.Provider>;
}

/**
 * Het Voorleesgebied zelf (EXPERIENCE.md "Overal"), één per pagina: leeg zolang er niets te melden
 * is, en het verdwijnt nooit uit de pagina. Twee delen: de toestand die op het scherm staat, en de
 * laatste aankondiging van een bediening (weekwissel, werksoortwissel, de rand van de Weekkiezer).
 * Een nieuwe sleutel per aankondiging vervangt het tekstknooppunt, zodat een herhaalde randzin
 * opnieuw wordt voorgelezen.
 */
export function Toestandsmelding() {
  const { zin, aankondiging } = useContext(Context);

  return (
    // `aria-atomic="false"`: `role="status"` leest anders bij elke verandering het hele gebied opnieuw,
    // en dan klinkt een oude weekzin mee met elke nieuwe toestand.
    <p aria-atomic="false" aria-live="polite" className="sr-only" data-testid="werkkwaliteit-toestandsmelding" role="status">
      {zin !== '' && <span>{zin}</span>}
      {zin !== '' && aankondiging.zin !== '' && ' '}
      {aankondiging.zin !== '' && <span key={aankondiging.sleutel}>{aankondiging.zin}</span>}
    </p>
  );
}

/** Zet één zin in het Voorleesgebied, voor een bediening die iets veranderde (of juist niet kon). */
export function useVoorlees(): (zin: string) => void {
  return useContext(Context).kondigAan;
}

/** Meldt zolang het component leeft welke toestand er op het scherm staat. */
export function useMeldToestand(zin: string): void {
  const { meld } = useContext(Context);
  useLayoutEffect(() => {
    meld(zin);
    return () => meld(null);
  }, [meld, zin]);
}
